/**
 * A card to display at the end of a video showcasing the next piece of
 * content, as well as the points earned.
 */
import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {
    enums,
    withConversionContext,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import type {EndOfVideoAttributionType} from "@khan/content-types";
import {pureXsMax} from "@khan/shared-styles";
import {isInIframe} from "@khan/urls";

import teacherContentUpsell from "#content-library-upsells-package/teacher-content-upsell.ts";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {
    deprecatedTypography,
    fonts,
} from "#shared-styles-package/deprecated-typography.ts";
import type {TutorialNavContent} from "#tutorial-types-package/types.ts";

import CountdownButton from "./countdown-button.tsx";
import EndOfVideoAttribution from "./end-of-video-attribution.tsx";

const nextIcon = `M7.7,1.3c0-0.7,0.6-1.2,1.2-1.2c0.7,0,1.2,0.6,1.2,1.2v7.3c0,0.7-0.6,
    1.2-1.2,1.2c-0.7,0-1.2-0.6-1.2-1.2V1.3z M0.2,0.9c0-0.4,0.3-0.6,0.6-0.3
    l5.5,4c0.3,0.2,0.3,0.6,0,0.9l-5.5,4C0.5,9.7,0.2,9.6,0.2,9.1V0.9z`;
const replayIcon = `M9.8,1v2.8c0,0.1,0,0.2-0.1,0.3C9.6,4.2,9.5,4.2,9.4,4.2H6.6c-0.2,
    0-0.3-0.1-0.4-0.2c-0.1-0.2,0-0.3,0.1-0.4l0.9-0.9C6.6,2.1,5.8,1.8,5,1.8
    C4.6,1.8,4.2,1.9,3.8,2C3.4,2.2,3,2.4,2.7,2.7C2.4,3,2.2,3.4,2,3.8C1.9,
    4.2,1.8,4.6,1.8,5c0,0.4,0.1,0.8,0.3,1.2c0.2,0.4,0.4,0.7,0.7,1C3,7.6,
    3.4,7.8,3.8,8C4.2,8.1,4.6,8.2,5,8.2c0.5,0,1-0.1,1.4-0.3C6.9,7.7,7.2,
    7.4,7.5,7c0,0,0.1-0.1,0.1-0.1c0.1,0,0.1,0,0.2,0.1l0.9,0.9c0,0,0.1,0.1,
    0.1,0.1c0,0.1,0,0.1,0,0.1c-0.5,0.6-1,1-1.7,1.3C6.4,9.7,5.7,9.8,5,
    9.8c-0.7,0-1.3-0.1-1.9-0.4C2.5,9.2,2,8.8,1.6,8.4C1.2,8,0.8,7.5,0.6,
    6.9C0.3,6.3,0.2,5.7,0.2,5c0-0.7,0.1-1.3,0.4-1.9c0.3-0.6,0.6-1.1,1-1.5s
    0.9-0.8,1.5-1C3.7,0.3,4.3,0.2,5,0.2c0.6,0,1.2,0.1,1.8,0.3c0.6,0.2,1.1,
    0.6,1.5,1l0.8-0.8c0.1-0.1,0.3-0.2,0.4-0.1C9.7,0.7,9.8,0.8,9.8,1`;

const END_OF_VIDEO_ATTRIBUTION_MS = 4000;

type OwnProps = {
    isLoggedIn: boolean;
    nextContentItem: TutorialNavContent | null | undefined;
    onButtonClick: () => void;
    onViewAllClick: () => void;
    pointsEarned: number;
    url?: string;
    attribution: EndOfVideoAttributionType | null | undefined;
};

type Props = OwnProps & WithLocationProps & WithMarkConversionProps;

type State = {
    showSponsorRecognition: boolean;
};

class EndOfVideoCard extends React.Component<Props, State> {
    state: State = {
        showSponsorRecognition: !!this.props.attribution,
    };

    componentDidMount() {
        this.props.markConversion({id: "video_end_card_seen"});
        if (!this.props.isLoggedIn) {
            this.props.markConversion({id: "video_end_card_signin_seen"});
        }
    }

    handleButtonClick: (e?: React.SyntheticEvent | null | undefined) => void = (
        e,
    ) => {
        e && e.preventDefault();
        this.props.onButtonClick();
        this.props.markConversion({
            id: "video_end_card_cta",
            extra: {
                isPhantom: !this.props.isLoggedIn,
                type: this.props.nextContentItem ? "next" : "replay",
            },
        });
    };

    handleViewAllClick: (e: React.SyntheticEvent) => void = (e) => {
        e.preventDefault();
        this.props.onViewAllClick();
    };

    logSignInClicked: (e: React.SyntheticEvent) => void = (e) => {
        this.props.markConversion({id: "video_end_card_signin_clicked"});
    };

    renderEarnings(): React.ReactNode {
        const {pointsEarned} = this.props;

        // It's a little awkward to say "You earned 0 points!" so how about let's just not.
        if (!pointsEarned) {
            return null;
        }

        const pointsPart = (
            <span className={css(styles.earned)}>
                <Trans>{pointsEarned} energy points</Trans>
            </span>
        );

        return <Trans>You earned {pointsPart}!</Trans>;
    }

    renderIcon(label: string, icon: string): React.ReactNode {
        const {isLoggedIn, nextContentItem} = this.props;

        const className = css(
            styles.icon,
            !isLoggedIn && styles.iconTiny,
            styles.pulse,
        );

        return (
            <div className={className}>
                <CountdownButton
                    ariaLabel={label}
                    background={color.blue}
                    cancelled={!nextContentItem}
                    icon={icon}
                    onComplete={this.handleButtonClick}
                />
            </div>
        );
    }

    renderUpNext(): React.ReactNode {
        const {nextContentItem} = this.props;

        if (nextContentItem) {
            return (
                <div className={css(styles.upNext)}>
                    <button
                        className={css(styles.iconLabel, styles.upNextLabel)}
                        onClick={this.handleButtonClick}
                    >
                        {t`Up next`}
                    </button>
                    <div className={css(styles.upNextTitle)}>
                        {nextContentItem.translatedTitle}
                    </div>
                    {this.renderIcon(t`Play the next video`, nextIcon)}
                </div>
            );
        }
        return (
            <div className={css(styles.upNext)}>
                {this.renderIcon(t`Replay this video`, replayIcon)}
                <button
                    className={css(styles.iconLabel)}
                    onClick={this.handleButtonClick}
                >
                    {t`Replay this video`}
                </button>
            </div>
        );
    }

    renderMain(): React.ReactNode {
        return <div className={css(styles.main)}>{this.renderUpNext()}</div>;
    }

    render(): React.ReactNode {
        const {
            isLoggedIn,
            location: {
                queryParams: {forceContentUpsellPopup},
            },
        } = this.props;

        const suppressSignupLink = teacherContentUpsell.shouldShowPopup(
            forceContentUpsellPopup,
        );

        const buttonCopy = t`Sign in to save your progress`;
        const buttonHref = "/signup";
        const buttonId = "signin-button";

        const signupBtn = (
            <Button
                id={buttonId}
                style={styles.signup}
                href={buttonHref}
                onClick={this.logSignInClicked}
            >
                {buttonCopy}
            </Button>
        );

        if (this.state.showSponsorRecognition) {
            return (
                <div className={css(styles.container)}>
                    {/**
                     * TODO: (somewhatabstract, JIRA-XXXX):
                     * Cannot create EndOfVideoAttribution element because:
                     * • property logoCaption is missing in props [1] but
                     *   exists in EndOfVideoAttributionType [2].
                     * • ... 1 more error.
                     */}
                    {/* @ts-expect-error - TS2769 - No overload matches this call. */}
                    <EndOfVideoAttribution
                        {...this.props.attribution}
                        duration={END_OF_VIDEO_ATTRIBUTION_MS}
                        onComplete={() => {
                            this.setState({showSponsorRecognition: false});
                        }}
                    />
                </div>
            );
        }
        return (
            <div className={css(styles.container)}>
                <div className={css(styles.top)}>
                    {!suppressSignupLink && this.renderEarnings()}
                    {!suppressSignupLink &&
                        !isInIframe() &&
                        !isLoggedIn &&
                        signupBtn}
                </div>

                {this.renderMain()}
            </div>
        );
    }
}

type ExportProps = WithoutMarkConversion<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof EndOfVideoCard,
            React.ComponentProps<typeof EndOfVideoCard>
        >
    >
>;

const EndOfVideoCardWithLearningTime: React.ComponentType<ExportProps> =
    compose(
        withLocation,
        withConversionContext({
            activity: enums.Activity.WATCHING,
        }),
        withMarkConversion,
    )(EndOfVideoCard);

export default EndOfVideoCardWithLearningTime;

const mobilePlayerMaxWidth = parseInt(pureXsMax);

// The player can also be <=320px under a vertical constraint. What is the
// height at which the player is 320px wide and should use mobile styles?
const aspectRatio = 16 / 9;
const playerHeightPercentage = 0.6; // Player is <=60% of viewport height
const mobilePlayerMaxHeight = Math.floor(
    mobilePlayerMaxWidth / aspectRatio / playerHeightPercentage,
);

// Finally, build two queries (which are inverses of each other) to use.
const mobilePlayerMediaQuery =
    "@media " +
    `(max-width: ${mobilePlayerMaxWidth}px), ` +
    `(max-height: ${mobilePlayerMaxHeight}px)`;

const mobilePlayerXSmallMediaQuery =
    "@media (max-width: 374px), (max-height: 351px)";

const pulse = {
    from: {transform: "scale(1)"},
    to: {transform: "scale(1.1)"},
} as const;

const styles = StyleSheet.create({
    container: {
        ...deprecatedTypography.bodySmall,
        alignItems: "center",
        background: "rgba(17, 17, 17, 0.8)",
        color: color.white,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        justifyContent: "flex-start",
        position: "relative",
        textAlign: "center",
        WebkitFontSmoothing: "antialiased",
    },

    main: {
        flexGrow: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },

    earned: {
        fontFamily: "inherit",
        fontWeight: "bold",
        textDecoration: "underline",
    },

    top: {
        paddingTop: 16,

        [mobilePlayerMediaQuery]: {
            ...deprecatedTypography.labelSmall,
            paddingTop: 10,
        },
    },

    signup: {
        display: "flex",
        marginTop: 18,
        [mobilePlayerMediaQuery]: {
            marginTop: 6,
        },
    },

    icon: {
        alignItems: "center",
        borderRadius: "50%",
        display: "flex",
        flexShrink: 0,
        height: 78,
        justifyContent: "center",
        margin: "18px auto 9px",
        width: 78,

        [mobilePlayerMediaQuery]: {
            height: 46,
            margin: "8px auto 0",
            width: 46,
        },
    },

    // Shrink the mobile icon even further on the smallest screens
    // when the "sign in" button is being shown.
    iconTiny: {
        // @ts-expect-error - TS2322 - Type '{ "@media (max-width: 374px), (max-height: 351px)": { height: number; margin: string; width: number; }; }' is not assignable to type 'CSSProperties'.
        [mobilePlayerXSmallMediaQuery]: {
            height: 30,
            margin: "4px auto 0",
            width: 30,
        },
    },

    pulse: {
        animationDirection: "alternate",
        animationDuration: "1.5s",
        animationIterationCount: "infinite",
        // @ts-expect-error - TS2322 - Type '{ readonly from: { readonly transform: "scale(1)"; }; readonly to: { readonly transform: "scale(1.1)"; }; }' is not assignable to type 'AnimationName | undefined'.
        animationName: pulse,
        animationTimingFunction: "ease-in",
    },

    upNext: {
        ...deprecatedTypography.labelMedium,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        fontFamily: fonts.regular,
        justifyContent: "center",

        [mobilePlayerMediaQuery]: {
            ...deprecatedTypography.labelSmall,
        },
    },

    upNextLabel: {
        [`@media (max-width: ${pureXsMax}), (max-height: 351px)`]: {
            display: "none",
        },
    },

    iconLabel: {
        background: "none",
        border: 0,
        color: "inherit",
        cursor: "pointer",
        display: "inline-block",
        fontSize: "inherit",
        margin: "0 auto",
        padding: "6px 10px",

        ":hover": {
            background: "rgba(255, 255, 255, 0.25)",
        },
    },

    upNextTitle: {
        ...deprecatedTypography.labelLarge,
        marginBottom: 12,
        marginTop: 4,

        [mobilePlayerMediaQuery]: {
            ...deprecatedTypography.labelMedium,
            marginBottom: 0,
            marginTop: 0,
        },

        // @ts-expect-error - TS2322 - Type '{ marginBottom: number; marginTop: number; "@media (max-width: 374px), (max-height: 351px)": { alignContent?: Property.AlignContent | undefined; alignItems?: Property.AlignItems | undefined; ... 804 more ...; ":hover"?: _CSSProperties | undefined; }; ... 804 more ...; ":hover"?: _CSSProperties | undefined; }' is not assignable to type 'CSSProperties'.
        [mobilePlayerXSmallMediaQuery]: {
            ...deprecatedTypography.labelSmall,
        },
    },
});
